import { useUnit } from 'effector-react';
import $$api from 'models/api';
import $$auth from 'models/auth';
import $$ep from 'models/ep';
import { $global, storage } from 'models/store-global';
import { useEffect } from 'react';

import { DocDropdownOption } from './document-page';

import { Frame } from 'components/StyledTemplates/StyledTemplates';
import {
    DocumentsGrid,
    DocumentsSectionHeader,
    DocumentsSectionName,
} from 'components/pages/Dashboard/common/CommunityPageStyles';
import { DashboardButton } from 'components/pages/Dashboard/common/DashboardPageStyles';
import DocumentsLocation from 'components/pages/Dashboard/common/DocumentsLocation';
import EpActionsBlock from 'components/pages/Dashboard/common/EpActionsBlock';
import DocumentBlock from 'components/pages/Dashboard/documents/DocumentBlock';
import NewDocumentModal from 'components/pages/Dashboard/documents/NewDocumentModal';

import DashboardService from 'services/DashboardService';
import DocumentDownloadService from 'services/DocumentDownloadService';
import DocumentsService from 'services/DocumentsService';
import ModalsService from 'services/ModalsService';

import useDocumentTitle from 'hooks/useDocumentTitle';
import useExecutionSteps from 'hooks/useExecutionSteps';
import useResources from 'hooks/useResources';

import { MyStore } from 'types/Store';
import { Document } from 'types/zod/document';

const DocumentsPage = () => {
    const { t } = useResources();
    const documents = useUnit($global.map((state) => state.community?.docs || []));
    const estatePlan = useUnit($$ep.stores.$epQInstance);
    const epAnswers = useUnit($$ep.stores.$epAnswers);

    const selectedParticipant = useUnit(
        $global.map((state) =>
            state?.community?.realm?.find((i) => i.participant_id === state?.temp?.selected_community_participant),
        ),
    );

    const visibleDocuments = documents.filter((document) =>
        document?.ownership?.includes?.(selectedParticipant?.participant_id || -1),
    );

    const isAdmin = useUnit($$auth.$systemRoles.map((systemRoles) => systemRoles?.includes('admin') || false));

    const { steps: options } = useExecutionSteps();

    const isEpSigned = (options.find((option) => option.id === 'signed') || {}).done || false;
    const lastSignedDate = epAnswers?.hidden?.last_signed_date;

    const isDocSigned = (document: Document) => {
        return isEpSigned || +lastSignedDate > document.creation_date;
    };

    useEffect(() => {
        (async () => {
            if (estatePlan?.questionnaire_instance_id) {
                const docTypes = await $$api.qInstances.getDocumentTypesForQInstanceFx(
                    estatePlan?.questionnaire_instance_id,
                );
                storage.set(
                    (store): MyStore => ({
                        ...store,
                        temp: {
                            ...(store.temp || {}),
                            documents_in_progress: {
                                ...(store.temp?.documents_in_progress || {}),
                                documentTypes: docTypes.document_types,
                            },
                        },
                    }),
                );
                await $$api.qInstances.getDocumentsForQInstanceFx(estatePlan.questionnaire_instance_id);
            }
        })();
    }, [estatePlan?.questionnaire_instance_id]);

    useDocumentTitle(`Documents`);

    const handlers = {
        selectFile: ({ document_id }: any) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.onchange = async (e: any) => {
                const file = e.target.files?.[0];
                try {
                    storage.set(
                        (store): MyStore => ({
                            ...store,
                            uploading: {
                                ...(store.uploading || {}),
                                [document_id]: true,
                            },
                        }),
                    );
                    await DashboardService.uploadSignedDocument(document_id, file);
                } finally {
                    storage.set((store): MyStore => {
                        delete store.uploading?.[document_id];
                        return store;
                    });
                }
                await $$api.participants.getCommunityFx(undefined);
            };
            input.click();
        },
    };

    const docOptions = (document: Document): DocDropdownOption[] => {
        return [
            {
                children: t('newDashboardPage.print'),
                onClick: async () => {
                    if (
                        document.document_type_code === 'MAD_Instr' ||
                        document.document_type_code === 'MAD_Instr_Spouse'
                    ) {
                        await DocumentDownloadService.downloadDocx(document.document_id);
                    } else {
                        await DocumentDownloadService.download(document.document_id);
                    }
                },
            },
            {
                children: t('newDashboardPage.uploadSignedForm'),
                onClick: () => {
                    handlers.selectFile(document);
                },
                hidden: !DocumentsService.isSignable(document),
            },
        ].filter((option) => option.hidden !== true);
    };

    return (
        <>
            {isAdmin && <NewDocumentModal />}
            <DocumentsSectionHeader>
                <DocumentsSectionName className="docs-section-header text-maforestgreen">
                    {t('newDashboardPage.estatePlanDocuments')}
                </DocumentsSectionName>
                <EpActionsBlock />
            </DocumentsSectionHeader>
            {!visibleDocuments.some((document: Document) => document.category_code === 'estate_plan') && (
                <Frame extra="width: 100%; align-items: flex-start;">
                    {t(`newDashboardPage.emptyEstatePlanDocumentsPlaceholder`)}
                </Frame>
            )}
            <DocumentsGrid className="mb-5">
                {visibleDocuments
                    .filter((document) => document.category_code === 'estate_plan')
                    .map((document) => {
                        return (
                            <DocumentBlock
                                key={document.document_id}
                                document={document}
                                isSigned={isDocSigned(document)}
                                options={docOptions(document)}
                                headerColor={(props: any) => props.theme.primary}
                                allowScanUpload={true}
                            />
                        );
                    })}
            </DocumentsGrid>

            <DocumentsSectionHeader>
                <DocumentsSectionName className="text-mapersimmon">
                    {t('newDashboardPage.supportingDocuments')}
                </DocumentsSectionName>
                {isAdmin && (
                    <DashboardButton
                        data-qaid="new_document_button"
                        variant="secondary"
                        onClick={() => {
                            ModalsService.showModal('NEW_DOCUMENT');
                        }}
                    >
                        {t(`newDashboardPage.new`)}
                    </DashboardButton>
                )}
            </DocumentsSectionHeader>
            <DocumentsGrid extra="margin-bottom: 30px;">
                {!visibleDocuments.some((document) => document.category_code === 'supporting') && (
                    <Frame>{t(`newDashboardPage.emptySupportingDocumentsPlaceholder`)}</Frame>
                )}
                {visibleDocuments
                    .filter((document: Document) => document.category_code === 'supporting')
                    .map((document: Document) => {
                        return (
                            <DocumentBlock
                                key={document.document_id}
                                document={document}
                                isSigned={isDocSigned(document)}
                                options={docOptions(document)}
                                headerColor={(props: any) => props.theme.permission}
                                allowScanUpload={true}
                                showStatus={false}
                            />
                        );
                    })}
            </DocumentsGrid>

            {isEpSigned && <DocumentsLocation />}
        </>
    );
};

export default DocumentsPage;
